export interface SignAsset {
  kind: 'image' | 'video';
  url: string;
  displayMs: number;
  transitionMs: number;
}

export class SignRenderer {
  private stage: HTMLDivElement;
  private layerA: HTMLImageElement | HTMLVideoElement;
  private layerB: HTMLImageElement | HTMLVideoElement;
  private active: 'a' | 'b' = 'a';
  private placeholder: HTMLDivElement;

  constructor(parent: HTMLElement) {
    this.stage = document.createElement('div');
    this.stage.className = 'sign-stage kenburns';
    this.placeholder = document.createElement('div');
    this.placeholder.className = 'sign-placeholder';
    this.placeholder.textContent = 'Awaiting initialization';
    this.layerA = this.makeImageLayer();
    this.layerB = this.makeImageLayer();
    this.stage.append(this.placeholder, this.layerA, this.layerB);
    parent.append(this.stage);
  }

  show(asset: SignAsset | null): void {
    if (!asset) {
      this.placeholder.style.display = 'grid';
      this.layerA.classList.remove('is-active');
      this.layerB.classList.remove('is-active');
      return;
    }
    this.placeholder.style.display = 'none';
    const next = this.active === 'a' ? 'b' : 'a';
    const incoming = this.swapElement(next, asset);
    const outgoing = next === 'a' ? this.layerB : this.layerA;
    // Apply per-item transition duration to both layers so the cross-fade matches
    // the rotation item's transitionMs rather than the CSS default of 0.5 s.
    incoming.style.transition = `opacity ${asset.transitionMs}ms ease`;
    outgoing.style.transition = `opacity ${asset.transitionMs}ms ease`;
    requestAnimationFrame(() => {
      incoming.classList.add('is-active');
      outgoing.classList.remove('is-active');
      this.active = next;
    });
  }

  private swapElement(slot: 'a' | 'b', asset: SignAsset): HTMLElement {
    const cur = slot === 'a' ? this.layerA : this.layerB;
    const need: 'image' | 'video' = asset.kind;
    const have: 'image' | 'video' = cur.tagName === 'VIDEO' ? 'video' : 'image';
    let target = cur;
    if (have !== need) {
      const replacement = need === 'video' ? this.makeVideoLayer() : this.makeImageLayer();
      cur.replaceWith(replacement);
      if (slot === 'a') this.layerA = replacement; else this.layerB = replacement;
      target = replacement;
    }
    if (target instanceof HTMLImageElement) {
      target.src = asset.url;
    } else {
      target.src = asset.url;
      target.loop = true;
      target.muted = true;
      target.playsInline = true;
      target.autoplay = true;
      void target.play().catch(() => { /* ignore autoplay block */ });
    }
    return target;
  }

  private makeImageLayer(): HTMLImageElement {
    const el = document.createElement('img');
    el.className = 'sign-layer';
    return el;
  }

  private makeVideoLayer(): HTMLVideoElement {
    const el = document.createElement('video');
    el.className = 'sign-layer';
    el.muted = true; el.playsInline = true; el.loop = true; el.autoplay = true;
    return el;
  }

  element(): HTMLDivElement { return this.stage; }
}
